import { useEffect, useState } from "react";
import useSeo from "@/seo/useSeo";
import { toast } from "sonner";
import { Mail, MapPin, Send, Loader2, Clock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";

export default function Contact() {
  const { t } = useI18n();
  useSeo("/contact");
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "Demande d'information", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const SUBJECTS = [
    ["Demande d'information", t("subj.info")],
    ["Réservation de groupe (10 personnes)", t("subj.group")],
    ["Disponibilités", t("subj.avail")],
    ["Laisser un avis", t("subj.review")],
    ["Autre", t("subj.other")],
  ];

  useEffect(() => {
    api.get("/settings/public").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(t("ct.toast"));
      setForm({ name: "", email: "", phone: "", subject: "Demande d'information", message: "" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8" data-testid="contact-page">
      <p className="eyebrow">{t("ct.eyebrow")}</p>
      <h1 className="display-xl mt-6 text-6xl text-bone sm:text-7xl">{t("ct.title1")}<br />{t("ct.title2")}</h1>
      <p className="mt-6 max-w-xl text-sm leading-loose text-bone/70">{t("ct.intro")}</p>

      <div className="mt-12 grid gap-12 lg:grid-cols-12">
        <form onSubmit={submit} className="panel space-y-5 p-7 sm:p-9 lg:col-span-7" data-testid="contact-form">
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-2 block font-medium text-bone">{t("ct.name")}</span>
              <input required className="field" data-testid="contact-name" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </label>
            <label className="block text-sm">
              <span className="mb-2 block font-medium text-bone">{t("ct.email")}</span>
              <input required type="email" className="field" data-testid="contact-email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </label>
            <label className="block text-sm">
              <span className="mb-2 block font-medium text-bone">{t("ct.phone")}</span>
              <input className="field" data-testid="contact-phone" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </label>
            <label className="block text-sm">
              <span className="mb-2 block font-medium text-bone">{t("ct.subject")}</span>
              <select className="field" data-testid="contact-subject" value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}>
                {SUBJECTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
          <label className="block text-sm">
            <span className="mb-2 block font-medium text-bone">{t("ct.message")}</span>
            <textarea required rows={6} className="field" data-testid="contact-message" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} />
          </label>
          <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="contact-submit">
            {loading ? <><Loader2 size={16} className="animate-spin" /> {t("ct.sending")}</> : <><Send size={15} /> {t("ct.send")}</>}
          </button>
          {sent && <p className="text-center text-sm text-bone" data-testid="contact-success">{t("ct.success")}</p>}
        </form>

        <aside className="lg:col-span-5">
          <div className="panel space-y-6 p-8" data-testid="contact-info">
            <div className="flex items-start gap-4">
              <MapPin className="mt-0.5 text-brass" size={18} />
              <div>
                <p className="font-medium text-bone">{t("ct.address")}</p>
                <p className="text-sm text-muted-foreground">{info?.address || "2 rue de la Gypserie, 25500 Les Fins"}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail className="mt-0.5 text-brass" size={18} />
              <div>
                <p className="font-medium text-bone">{t("ct.emailLabel")}</p>
                <p className="text-sm text-muted-foreground">{info?.contact_email || "contact@lafermecomtoise.com"}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Clock className="mt-0.5 text-brass" size={18} />
              <div>
                <p className="font-medium text-bone">{t("ct.checkinout")}</p>
                <p className="text-sm text-muted-foreground">{t("ct.checkinoutValue")}</p>
              </div>
            </div>
          </div>

          <iframe
            title={t("ct.map")}
            data-testid="contact-map"
            className="mt-6 h-72 w-full border border-white/10"
            src="https://www.openstreetmap.org/export/embed.html?bbox=6.55%2C47.03%2C6.72%2C47.11&layer=mapnik&marker=47.0667%2C6.6333"
          />
        </aside>
      </div>
    </div>
  );
}
